import { useContext } from 'react';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { AuthContext } from '../providers/AuthProvider';

const FLAG_NAMES = {
  groupBase: 'devx-group-base-enable',
  sidebarType: 'devx-feature-enable-sidebarType',
  aiEnabledAPI: 'devx-ui-ai-enabled-api-enable',
  aiInSdlc: 'devx-feature-enable-ai-in-sdlc',
  permitToBuild: 'devx-feature-enable-permit-to-build-p2b',
  servicesAndTools: 'devx-feature-enable-services-and-tools',
  informationExchange: 'devx-feature-enable-information-exchange',
  qeAgents: 'devx-feature-enable-qe-agents',
  qeScoreAgent: 'devx-feature-enable-qe-score-agent',
  iqeAgent: 'devx-feature-enable-iqe-agent',
  dataInsights: 'devx-feature-enable-data-insights',
  pocHub: 'devx-feature-enable-poc-hub',
  provisioningCenter: 'devx-feature-enable-provisioning-center',
  promptStore: 'devx-feature-enable-prompt-store',
  githubCostCenter: 'devx-feature-enable-github-cost-center',
  aiAssistantsCatalog: 'devx-feature-enable-ai-assistants-catalog',
  ellisAiDntChat: 'devx-feature-enable-ellis-ai-dnt-chat',
} as const;

type FeatureFlagKey = keyof typeof FLAG_NAMES;

// Maps each sidebar menu label directly to its Split flag name, so callers
// don't need to know about FeatureFlagKey internals.
export const LABEL_FLAG_MAP: Record<string, string> = {
  'AI in SDLC': FLAG_NAMES.aiInSdlc,
  'Permit to build (P2B)': FLAG_NAMES.permitToBuild,
  'Services & Tools': FLAG_NAMES.servicesAndTools,
  'Information Exchange': FLAG_NAMES.informationExchange,
  'QE Agents': FLAG_NAMES.qeAgents,
  'QE Score Agent': FLAG_NAMES.qeScoreAgent,
  'IQE Agent': FLAG_NAMES.iqeAgent,
  'Data Insights': FLAG_NAMES.dataInsights,
  'POC Hub': FLAG_NAMES.pocHub,
  'Provisioning Center': FLAG_NAMES.provisioningCenter,
  'Prompt Store': FLAG_NAMES.promptStore,
  'GitHub Cost Center': FLAG_NAMES.githubCostCenter,
  'AI Assistants Catalog': FLAG_NAMES.aiAssistantsCatalog,
  'Ellis AI D&T Chat': FLAG_NAMES.ellisAiDntChat,
};

export const FEATURE_LABELS = Object.keys(LABEL_FLAG_MAP);

const useSplitKey = () => {
  const { profEmail } = useContext(AuthContext);
  return profEmail?.toLowerCase() || 'anonymous';
};

const useFeatureEnabled = (
  flagKey: FeatureFlagKey,
  fallbackEnabled: boolean,
) => {
  const flagName = FLAG_NAMES[flagKey];
  const splitKey = useSplitKey();

  const { treatments, isReady, isReadyFromCache } = useSplitTreatments({
    names: [flagName],
    splitKey,
    updateOnSdkUpdate: true,
  });

  const treatment = treatments[flagName]?.treatment ?? 'control';
  const shouldApplyFlag = isReady || isReadyFromCache;

  return !shouldApplyFlag ? fallbackEnabled : treatment === 'on';
};

const useFeatureConfig = (flagKey: FeatureFlagKey) => {
  const flagName = FLAG_NAMES[flagKey];
  const splitKey = useSplitKey();

  const { treatments, isReady, isReadyFromCache } = useSplitTreatments({
    names: [flagName],
    splitKey,
    updateOnSdkUpdate: true,
  });

  const rawConfig = treatments[flagName]?.config;
  let config: any = {};
  if (rawConfig) {
    try {
      config =
        typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
    } catch {
      config = {};
    }
  }

  return { config, isReady: isReady || isReadyFromCache };
};

export const useDevxGroupBaseEnabled = () =>
  useFeatureEnabled('groupBase', false);
export const useAiInSdlcEnabled = () => useFeatureEnabled('aiInSdlc', false);
export const usePermitToBuildEnabled = () =>
  useFeatureEnabled('permitToBuild', false);
export const useServicesAndToolsEnabled = () =>
  useFeatureEnabled('servicesAndTools', false);
export const useInformationExchangeEnabled = () =>
  useFeatureEnabled('informationExchange', false);
export const useQEAgentsEnabled = () => useFeatureEnabled('qeAgents', false);
export const useQEScoreAgentEnabled = () =>
  useFeatureEnabled('qeScoreAgent', false);
export const useIQEAgentEnabled = () => useFeatureEnabled('iqeAgent', false);
export const useDataInsightsEnabled = () =>
  useFeatureEnabled('dataInsights', false);
export const usePocHubEnabled = () => useFeatureEnabled('pocHub', false);
export const useProvisioningCenterEnabled = () =>
  useFeatureEnabled('provisioningCenter', false);
export const usePromptStoreEnabled = () =>
  useFeatureEnabled('promptStore', false);
export const useGithubCostCenterEnabled = () =>
  useFeatureEnabled('githubCostCenter', false);
export const useAiAssistantsCatalogEnabled = () =>
  useFeatureEnabled('aiAssistantsCatalog', false);
export const useEllisAiDntChatEnabled = () =>
  useFeatureEnabled('ellisAiDntChat', false);
export const useGitHubCostCenterEditBudgetEnabled = () => {
  const isFeatureEnabled = useGithubCostCenterEnabled();
  if (isFeatureEnabled) {
    const { config } = useFeatureConfig('githubCostCenter');
    if (config?.editBudget === true) {
      return true;
    }
  }
  return false;
};

export const useSidebarTypeConfig = () => useFeatureConfig('sidebarType');
export const useAiEnabledApiConfig = () => useFeatureConfig('aiEnabledAPI');

// Derives enabled state for every labelled feature from LABEL_FLAG_MAP.
// FEATURE_LABELS is a fixed, static list, so calling a hook per label in this
// stable order is safe under the Rules of Hooks.
export const useFeatureEnabledByLabel = (): Record<string, boolean> => {
  const splitKey = useSplitKey();
  const enabledByLabel: Record<string, boolean> = {};

  for (const label of FEATURE_LABELS) {
    const flagName = LABEL_FLAG_MAP[label];
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { treatments, isReady, isReadyFromCache } = useSplitTreatments({
      names: [flagName],
      splitKey,
      updateOnSdkUpdate: true,
    });
    const treatment = treatments[flagName]?.treatment ?? 'control';
    const shouldApplyFlag = isReady || isReadyFromCache;
    enabledByLabel[label] = shouldApplyFlag && treatment === 'on';
  }
  return enabledByLabel;
};
